import { useGeoPosition } from '@repo/geo-position';
import { LocationCrosshairsIcon, LocationCrosshairsOffIcon, Filters3Icon } from '@repo/icons';
import { isCenteredOn } from '@repo/map';
import type { TestId } from '@repo/test-id';
import { Badge, Button } from '@repo/ui';

import MapTypeButton from '@shared/components/MapTypeButton';
import { useMapTypeStore } from '@shared/stores/useMapTypeStore';
import { selectSelectedFiltersCount, useParkingSearchStore } from '@shared/stores/useParkingSearchStore';

interface Props {
  testId: TestId;
}

export default function ControlButtonGroup({ testId }: Props) {
  const {
    isKnown: isPositionKnown,
    position,
    isFollowing,
    setIsFollowing,
    repanFunction,
    request: requestGeoPosition,
  } = useGeoPosition();
  const selectedFiltersCount = useParkingSearchStore(selectSelectedFiltersCount);
  const mapCenter = useParkingSearchStore((state) => state.mapCenter);
  const toggleFilters = useParkingSearchStore((state) => state.toggleFilters);
  const mapType = useMapTypeStore((state) => state.mapType);
  const setMapType = useMapTypeStore((state) => state.setMapType);

  const isAlreadyAtPosition = position !== undefined && mapCenter !== undefined && isCenteredOn(mapCenter, position);

  const handleMoveOrFollow = (): void => {
    if (isAlreadyAtPosition) {
      setIsFollowing(true);
    } else if (repanFunction) {
      repanFunction();
    }
  };

  const handleStopFollowing = (): void => {
    setIsFollowing(false);
  };

  return (
    <div className="flex flex-row items-center gap-2">
      {!isPositionKnown ? (
        <Button
          className="bg-danger-soft"
          color="danger"
          variant="outline"
          size="lg"
          iconOnly
          leftIcon={LocationCrosshairsOffIcon}
          testId={testId.append('geoPositionHelpButton')}
          onClick={requestGeoPosition}
        />
      ) : isFollowing ? (
        <Button
          color="brand"
          iconOnly
          size="lg"
          leftIcon={LocationCrosshairsIcon}
          testId={testId.append('followPositionButton')}
          onClick={handleStopFollowing}
        />
      ) : (
        <Button
          color="tertiary"
          iconOnly
          size="lg"
          leftIcon={LocationCrosshairsIcon}
          testId={testId.append('moveMapToPositionButton')}
          onClick={handleMoveOrFollow}
        />
      )}
      <MapTypeButton mapType={mapType} onChange={setMapType} testId={testId.append('mapType')} />
      <div className="relative flex items-center justify-center md:hidden">
        <Button
          color="dark"
          size="lg"
          iconOnly
          leftIcon={Filters3Icon}
          onClick={toggleFilters}
          testId={testId.append('openFiltersButton')}
        />
        {selectedFiltersCount > 0 && (
          <Badge
            color="ghost"
            label={selectedFiltersCount.toString()}
            iconOnly
            circle
            className="bg-danger absolute translate-x-full -translate-y-full border-0 text-white"
            testId={testId.append('filtersCount')}
          />
        )}
      </div>
    </div>
  );
}
